import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tag, X, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface DiscountResult {
  code_id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  calculated_discount: number;
  final_price: number;
}

/** Call after successful payment to register usage */
export async function registerDiscountUsage(codeId: string, cohortId?: string) {
  await supabase.rpc('register_discount_usage', {
    _code_id: codeId,
    _cohort_id: cohortId ?? null,
  });
}

interface DiscountCodeInputProps {
  originalPrice: number;
  context: "cohort" | "subscription";
  cohortId?: string;
  onApply: (result: DiscountResult | null) => void;
}

export const DiscountCodeInput = ({
  originalPrice,
  context,
  cohortId,
  onApply,
}: DiscountCodeInputProps) => {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState<DiscountResult | null>(null);
  const [autoApplied, setAutoApplied] = useState(false);
  const autoTried = useRef(false);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  const applyCode = useCallback(
    async (rawCode: string, silent = false): Promise<boolean> => {
      const value = rawCode.trim().toUpperCase();
      if (!value) return false;
      setLoading(true);

      try {
        const { data: rows, error } = await (supabase as any).rpc('validate_discount_code', {
          _code: value,
          _context: context,
          _cohort_id: null,
        });
        const data = Array.isArray(rows) ? rows[0] : rows;

        if (error || !data) {
          if (!silent) toast.error("Código no válido, inactivo o expirado");
          return false;
        }

        // Specific cohort, user, expiration, max_uses, applies_to are all enforced server-side by validate_discount_code.
        if (cohortId && data.specific_cohort_id && data.specific_cohort_id !== cohortId) {
          if (!silent) toast.error("Este código no aplica para esta cohorte");
          return false;
        }

        // Calculate discount
        let discount = 0;
        if (data.discount_type === "percentage") {
          discount = (originalPrice * Number(data.discount_value)) / 100;
        } else {
          discount = Math.min(Number(data.discount_value), originalPrice);
        }

        const result: DiscountResult = {
          code_id: data.id,
          code: data.code,
          discount_type: data.discount_type,
          discount_value: Number(data.discount_value),
          calculated_discount: Math.round(discount * 100) / 100,
          final_price: Math.max(0, Math.round((originalPrice - discount) * 100) / 100),
        };

        setApplied(result);
        onApplyRef.current(result);
        if (!silent) toast.success("¡Código de descuento aplicado!");
        return true;
      } catch {
        if (!silent) toast.error("Error al validar el código");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [context, cohortId, originalPrice]
  );

  const handleApply = () => applyCode(code);

  /** Auto-apply the user's own reward code (referral welcome / referrer discount). */
  useEffect(() => {
    if (autoTried.current) return;
    if (!user?.id || applied || originalPrice <= 0) return;
    autoTried.current = true;

    (async () => {
      const { data, error } = await supabase
        .from('discount_codes')
        .select('code, expires_at, active, times_used, max_uses')
        .eq('specific_user_id', user.id)
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error || !data?.length) return;

      const now = Date.now();
      const candidate = data.find((c: any) => {
        const notExpired = !c.expires_at || new Date(c.expires_at).getTime() > now;
        const hasUses = (c.times_used ?? 0) < (c.max_uses ?? 1);
        return notExpired && hasUses;
      });
      if (!candidate) return;

      const ok = await applyCode(candidate.code, true);
      if (ok) {
        setAutoApplied(true);
        toast.success("Aplicamos tu descuento de bienvenida");
      }
    })();
  }, [user?.id, applied, originalPrice, applyCode]);

  const handleRemove = () => {
    setApplied(null);
    setAutoApplied(false);
    setCode("");
    onApplyRef.current(null);
  };


  if (applied) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground">
            {autoApplied ? 'Descuento de bienvenida' : 'Código'} <Badge variant="secondary" className="mx-1 text-xs">{applied.code}</Badge> aplicado
          </span>

          <span className="text-sm text-emerald-600 ml-1">
            -{applied.discount_type === "percentage" ? `${applied.discount_value}%` : `$${applied.discount_value}`}
          </span>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleRemove}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Código de descuento"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          className="pl-9"
          disabled={loading}
        />
      </div>
      <Button variant="outline" size="default" onClick={handleApply} disabled={loading || !code.trim()}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
      </Button>
    </div>
  );
};
